import * as Sentry from '@sentry/browser';
import { Integrations } from '@sentry/tracing';
import Vue from 'vue';
import VueRouter from 'vue-router';
import VueI18n from 'vue-i18n';
import VoerroTagsInput from '@voerro/vue-tagsinput';
import PortalVue from 'portal-vue';
import { marked } from 'marked';
import filters from './filters';
import store from './store';
import router from './router';
import GoogleAnalytics from './plugins/googleanalytics';
import queryHelper from './plugins/queryHelper';
import ovpFetch from './ovp-fetch';
import App from './main.vue';
import en from './locale/en.json';

// eslint-disable-next-line
import swal from 'sweetalert';

// eslint-disable-next-line
// import 'simplemde/dist/simplemde.min.css';
import '@voerro/vue-tagsinput/dist/style.css';
import './styles/index.css';


if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://821156349ceb45d1b7cb6cb391cb3b11@o2576.ingest.sentry.io/1217690',
    release: 'vue-frontend@1.0.0',
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
    ignoreErrors: [
      'Blocked a frame with origin',
      'Cannot read property \'getRemotePlayer\'',
      'Cannot read properties of undefined (reading \'getRemotePlayer\')',
      'Error: Cannot resolve iframe with unknown index "0"',
      'fullscreen error',
      'The operation is not supported',
      'ibFindAllVideos is not defined',
      'Cannot read property \'startLoad\' of null',
      'NotAllowedError: The video element is processing a Picture-in-Picture request.',
      'Error: IndexSizeError: The index is not in the allowed range.',
      'Error: InvalidStateError: The object is in an invalid state.',
    ],
  });
}

// require('./polyfills.js');

Vue.config.ignoredElements = ['streamify-liveshopping'];

Vue.use(VueRouter);
Vue.use(VueI18n);
Vue.use(filters);
Vue.use(PortalVue);

Vue.component('tags-input', VoerroTagsInput);

Vue.directive('click-outside', {
  bind: (elem, binding, vnode) => {
    const el = elem;

    if (document.querySelectorAll(`.${elem.classList[0]}`).length > 0) {
      document.body.dispatchEvent(new PointerEvent('click'));
    }

    el.event = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        vnode.context[binding.expression](event);
      }
    };
    document.body.addEventListener('click', el.event);
  },
  unbind: (el) => {
    document.body.removeEventListener('click', el.event);
  },
});

Vue.mixin({
  methods: {
    handleLink(event, path) {
      if (event.ctrlKey || event.metaKey) {
        // if click + ctrl
        const routeData = this.$router.resolve({ path });
        window.open(routeData.href, '_blank');
      } else {
        // if just click
        this.$router.push({ path });
      }
    },
  },
  computed: {
    /**
     * Returns the current user in store
     * @returns {?Object}
     */
    user() {
      return this.$store.getters['User/user'];
    },
    /**
     * Returns a boolean if the current account has an active subscription
     * @returns {Boolean}
     */
    activeSubscription() {
      return this.$store.getters['AccountManager/activeSubscription'];
    },
    /**
     * Returns a boolean if the current account has an active subscription plan
     * @returns {Boolean}
     */
    hasSubscriptionPlan() {
      return this.$store.getters['AccountManager/hasSubscriptionPlan'];
    },
    /**
     * Returns array of features enabled on account
     * @returns {Array}
     */
    featuresEnabled() {
      return this.$store.getters['AccountManager/featuresEnabled'];
    },
    /**
     * Return the current account
     * @returns {?Object}
     */
    account() {
      return this.$store.getters['AccountManager/account'];
    },
    /**
     * Return the current settings on an account
     * @returns {?Object}
     */
    accountSettings() {
      return this.$store.getters['AccountManager/accountSettings'];
    },
    /**
     * Returns a boolean if the user is assumed to an account or not
     * @returns {Boolean}
     */
    assumed() {
      return this.$store.getters['User/assumed'];
    },
    assumeParam() {
      return this.$store.getters['User/assumeParam'];
    },
    /**
     * Returns the current shopping-settings if shopping is enabled
     * @returns {?Object}
     */
    shoppingSettings() {
      return this.$store.getters['AccountManager/shoppingSettings'] || {};
    },
    /**
     * Return boolean if ppv is enabled
     * @return {Boolean}
     */
    ppvEnabled() {
      return this.$store.getters['AccountManager/ppvEnabled'];
    },
    /**
     * Returns boolean if liveshopping is enabled
     */
    liveshoppingEnabled() {
      const {
        shopping: {
          enabled = false,
        } = {},
      } = this.$store.getters['AccountManager/accountSettings'] || {};
      return enabled;
    },
    /**
     * Get the users permission
     * @return {?Number}
     */
    permission() {
      return this.$store.getters['User/permission'];
    },
    /**
     * Return the current accounts FQDN
     */
    fqdn() {
      const [fqdn] = (this.$store.getters['AccountManager/account'] || { fqdn: '' })?.fqdn.replace('streamingbolaget.se', 'streamify.io').split('.');
      return fqdn;
    },
    /**
     * Return the current accounts FQDN (long version)
     */
    longfqdn() {
      const fqdn = (this.$store.getters['AccountManager/account'] || { fqdn: '' })?.fqdn.replace('streamingbolaget.se', 'streamify.io');
      return fqdn;
    },
    /**
     * Return the current state the account is in
     * @returns {String}
     */
    accountStatusState() {
      return this.$store.getters['AccountManager/accountStatusState'];
    },
    /**
     * Returns a boolean if the user is a superuser or not
     * @returns {Boolean}
     */
    isSuperUser() {
      return this.user.account_id === 1;
    },
    /**
     * Returns a boolean if the user has access to assume accounts
     * @returns {Boolean}
     */
    assumable() {
      return [1, 6, 16].includes(this.permission);
    },
  },
  // eslint-disable-next-line
  data: () => {
    return {
      get productTypes() {
        return {
          sv: [],
          en: [
            { value: 'stream', string: 'Ticket' },
            { value: 'good', string: 'Goods' },
            { value: 'service', string: 'Service' },
          ],
        };
      },
      get stockType() {
        return {
          sv: [
            { value: 'infinite', string: 'Inget lagersaldo', selected: true },
            { value: 'finite', string: 'Aktivera lagersaldo' },
          ],
          en: [
            { value: 'infinite', string: 'No inventory', selected: true },
            { value: 'finite', string: 'Activate inventory' },
          ],
        };
      },
      get priceType() {
        return {
          sv: [
            // eslint-disable-next-line
            { value: 'one_time', string: 'One time', selected: true, description: 'Kunden debiteras en gång.'},
            // eslint-disable-next-line
            { value: 'recurring', string: 'Subscription', description: 'Kunden kommer att debiteras med ett återkommande intervall.' },
          ],
          en: [
            // eslint-disable-next-line
            { value: 'one_time', string: 'One time', selected: true, description: 'The customer will be charged once.'},
            // eslint-disable-next-line
            { value: 'recurring', string: 'Subscription', description: 'The customer will be charged at a recurring interval.' },
          ],
        };
      },
      get recurring() {
        return {
          sv: [],
          en: [
            // eslint-disable-next-line
            { value: 'day', string: 'Once a day', selected: false, description: 'Buyer will pay an amount each day' },
            // eslint-disable-next-line
            { value: 'week', string: 'Once a week', selected: false, description: 'Each week there will be a deposit to our account' },
            // eslint-disable-next-line
            { value: 'month', string: 'Once a month', selected: true, description: 'Monthly means each month' },
            // eslint-disable-next-line
            { value: 'year', string: 'Once a year', selected: false, description: 'Pay for a single year of access!' },
          ],
        };
      },
      get availableCurrencies() {
        return [
          'aed',
          'afn',
          'all',
          'amd',
          'ang',
          'aoa',
          'ars',
          'aud',
          'awg',
          'azn',
          'bam',
          'bbd',
          'bdt',
          'bgn',
          'bif',
          'bmd',
          'bnd',
          'bob',
          'brl',
          'bsd',
          'bwp',
          'bzd',
          'cad',
          'cdf',
          'chf',
          'clp',
          'cny',
          'cop',
          'crc',
          'cve',
          'czk',
          'djf',
          'dkk',
          'dop',
          'dzd',
          'eek',
          'egp',
          'etb',
          'eur',
          'fjd',
          'fkp',
          'gbp',
          'gel',
          'gip',
          'gmd',
          'gnf',
          'gtq',
          'gyd',
          'hkd',
          'hnl',
          'hrk',
          'htg',
          'huf',
          'idr',
          'ils',
          'inr',
          'isk',
          'jmd',
          'jpy',
          'kes',
          'kgs',
          'khr',
          'kmf',
          'krw',
          'kyd',
          'kzt',
          'lak',
          'lbp',
          'lkr',
          'lrd',
          'lsl',
          'ltl',
          'lvl',
          'mad',
          'mdl',
          'mga',
          'mkd',
          'mnt',
          'mop',
          'mro',
          'mur',
          'mvr',
          'mwk',
          'mxn',
          'myr',
          'mzn',
          'nad',
          'ngn',
          'nio',
          'nok',
          'npr',
          'nzd',
          'pab',
          'pen',
          'pgk',
          'php',
          'pkr',
          'pln',
          'pyg',
          'qar',
          'ron',
          'rsd',
          'rub',
          'rwf',
          'sar',
          'sbd',
          'scr',
          'sek',
          'sgd',
          'shp',
          'sll',
          'sos',
          'srd',
          'std',
          'svc',
          'szl',
          'thb',
          'tjs',
          'top',
          'try',
          'ttd',
          'twd',
          'tzs',
          'uah',
          'ugx',
          'usd',
          'uyu',
          'uzs',
          'vef',
          'vnd',
          'vuv',
          'wst',
          'xaf',
          'xcd',
          'xof',
          'xpf',
          'yer',
          'zar',
          'zmw',
        ];
      },
      get accountCountries() {
        return {
          sv: [
            { value: 'au', string: 'Australien', ibanPlaceholder: '' },
            { value: 'be', string: 'Belgien', ibanPlaceholder: 'BE71 0961 2345 6769' },
            { value: 'bg', string: 'Bulgarien', ibanPlaceholder: 'BG18 RZBB 9155 0123 4567 89' },
            { value: 'cy', string: 'Cypern', ibanPlaceholder: 'CY21 0020 0195 0000 3570 0123 4567' },
            { value: 'dk', string: 'Danmark', ibanPlaceholder: 'DK95 2000 0123 4567 89' },
            { value: 'ee', string: 'Estland', ibanPlaceholder: 'EE47 1000 0010 2014 5685' },
            { value: 'fi', string: 'Finland', ibanPlaceholder: 'FI14 1009 3000 1234 58' },
            { value: 'fr', string: 'Frankrike', ibanPlaceholder: 'FR76 3000 6000 0112 3456 7890 189' },
            { value: 'hk', string: 'Hong Kong', ibanPlaceholder: '' },
            { value: 'ie', string: 'Irland', ibanPlaceholder: 'IE64 IRCE 9205 0112 3456 78' },
            { value: 'it', string: 'Italien', ibanPlaceholder: 'IT60 X054 2811 1010 0000 0123 456' },
            { value: 'jp', string: 'Japan', ibanPlaceholder: '' },
            { value: 'ca', string: 'Kanada', ibanPlaceholder: '' },
            { value: 'lv', string: 'Lettland', ibanPlaceholder: 'LV97 HABA 0012 3456 7891 0' },
            { value: 'lt', string: 'Litauen', ibanPlaceholder: 'LT60 1010 0123 4567 8901' },
            { value: 'lu', string: 'Luxembourg', ibanPlaceholder: 'LU12 0010 0012 3456 7891' },
            { value: 'my', string: 'Malaysia', ibanPlaceholder: '' },
            { value: 'mt', string: 'Malta', ibanPlaceholder: 'MT31 MALT 0110 0000 0000 0000 0000 123' },
            { value: 'mx', string: 'Mexico', ibanPlaceholder: '' },
            { value: 'nl', string: 'Nederländerna', ibanPlaceholder: 'NL02 ABNA 0123 4567 89' },
            { value: 'nz', string: 'Nya Zeeland', ibanPlaceholder: '' },
            { value: 'no', string: 'Norge', ibanPlaceholder: 'NO83 3000 1234 567' },
            { value: 'pl', string: 'Polen', ibanPlaceholder: 'PL10 1050 0099 7603 1234 5678 9123' },
            { value: 'pt', string: 'Portugal', ibanPlaceholder: 'PT50 0027 0000 0001 2345 6783 3' },
            { value: 'ro', string: 'Rumänien', ibanPlaceholder: 'RO09 BCYP 0000 0012 3456 7890' },
            { value: 'ch', string: 'Schweiz', ibanPlaceholder: 'CH56 0483 5012 3456 7800 9' },
            { value: 'sg', string: 'Singapore', ibanPlaceholder: '' },
            { value: 'sk', string: 'Slovakien', ibanPlaceholder: 'SK89 7500 0000 0000 1234 5671' },
            { value: 'si', string: 'Slovenien', ibanPlaceholder: 'SI56 1920 0123 4567 892' },
            { value: 'es', string: 'Spanien', ibanPlaceholder: 'ES79 2100 0813 6101 2345 6789' },
            { value: 'gb', string: 'Storbritannien', ibanPlaceholder: 'GB33 BUKB 2020 1555 5555 55' },
            { value: 'se', string: 'Sverige', ibanPlaceholder: 'SE72 8000 0810 3400 0978 3242' },
            { value: 'cz', string: 'Tjeckien', ibanPlaceholder: 'CZ55 0800 0000 0012 3456 7899' },
            { value: 'de', string: 'Tyskland', ibanPlaceholder: 'DE75 5121 0800 1245 1261 99' },
            { value: 'us', string: 'USA', ibanPlaceholder: '' },
            { value: 'at', string: 'Österrike', ibanPlaceholder: 'AT48 3200 0000 1234 5864' },
          ],
          en: [
            { value: 'au', string: 'Australia', ibanPlaceholder: '' },
            { value: 'at', string: 'Austria', ibanPlaceholder: 'AT48 3200 0000 1234 5864' },
            { value: 'be', string: 'Belgium', ibanPlaceholder: 'BE71 0961 2345 6769' },
            { value: 'bg', string: 'Bulgaria', ibanPlaceholder: 'BG18 RZBB 9155 0123 4567 89' },
            { value: 'ca', string: 'Canada', ibanPlaceholder: '' },
            { value: 'cy', string: 'Cyprus', ibanPlaceholder: 'CY21 0020 0195 0000 3570 0123 4567' },
            { value: 'cz', string: 'Czech Republic', ibanPlaceholder: 'CZ55 0800 0000 0012 3456 7899' },
            { value: 'dk', string: 'Denmark', ibanPlaceholder: 'DK95 2000 0123 4567 89' },
            { value: 'ee', string: 'Estonia', ibanPlaceholder: 'EE47 1000 0010 2014 5685' },
            { value: 'fi', string: 'Finland', ibanPlaceholder: 'FI14 1009 3000 1234 58' },
            { value: 'fr', string: 'France', ibanPlaceholder: 'FR76 3000 6000 0112 3456 7890 189' },
            { value: 'de', string: 'Germany', ibanPlaceholder: 'DE75 5121 0800 1245 1261 99' },
            { value: 'hk', string: 'Hong Kong', ibanPlaceholder: '' },
            { value: 'ie', string: 'Ireland', ibanPlaceholder: 'IE64 IRCE 9205 0112 3456 78' },
            { value: 'it', string: 'Italy', ibanPlaceholder: 'IT60 X054 2811 1010 0000 0123 456' },
            { value: 'jp', string: 'Japan', ibanPlaceholder: '' },
            { value: 'lv', string: 'Latvia', ibanPlaceholder: 'LT60 1010 0123 4567 8901' },
            { value: 'lt', string: 'Lithuania', ibanPlaceholder: 'LT60 1010 0123 4567 8901' },
            { value: 'lu', string: 'Luxembourg', ibanPlaceholder: 'LU12 0010 0012 3456 7891' },
            { value: 'my', string: 'Malaysia', ibanPlaceholder: '' },
            { value: 'mt', string: 'Malta', ibanPlaceholder: 'MT31 MALT 0110 0000 0000 0000 0000 123' },
            { value: 'mx', string: 'Mexico', ibanPlaceholder: '' },
            { value: 'nl', string: 'Netherlands', ibanPlaceholder: '' },
            { value: 'nz', string: 'New Zealand', ibanPlaceholder: '' },
            { value: 'no', string: 'Norway', ibanPlaceholder: 'NO83 3000 1234 567' },
            { value: 'pl', string: 'Poland', ibanPlaceholder: 'PL10 1050 0099 7603 1234 5678 9123' },
            { value: 'pt', string: 'Portugal', ibanPlaceholder: 'PT50 0027 0000 0001 2345 6783 3' },
            { value: 'ro', string: 'Romania', ibanPlaceholder: 'RO09 BCYP 0000 0012 3456 7890' },
            { value: 'sg', string: 'Singapore', ibanPlaceholder: '' },
            { value: 'sk', string: 'Slovakia', ibanPlaceholder: 'SK89 7500 0000 0000 1234 5671' },
            { value: 'si', string: 'Slovenia', ibanPlaceholder: 'SI56 1920 0123 4567 892' },
            { value: 'es', string: 'Spain', ibanPlaceholder: 'ES79 2100 0813 6101 2345 6789' },
            { value: 'se', string: 'Sweden', ibanPlaceholder: 'SE72 8000 0810 3400 0978 3242' },
            { value: 'ch', string: 'Switzerland', ibanPlaceholder: 'CH56 0483 5012 3456 7800 9' },
            { value: 'gb', string: 'United Kingdom', ibanPlaceholder: 'GB33 BUKB 2020 1555 5555 55' },
            { value: 'us', string: 'United States', ibanPlaceholder: '' },
          ],
        };
      },
      get accountBranches() {
        return {
          sv: [
            { value: 'musik', string: 'Musik' },
            { value: 'sport', string: 'Sport' },
            { value: 'teater', string: 'Teater' },
            { value: 'workshops', string: 'Workshops' },
          ],
          en: [
            { value: 'music', string: 'Music' },
            { value: 'sport', string: 'Sport' },
            { value: 'theater', string: 'Theater' },
            { value: 'workshops', string: 'Workshops' },
          ],
        };
      },
    };
  },
});

window.ovpFetch = ovpFetch.ovpFetch;
window.xhrPut = ovpFetch.xhrPut;
window.marked = marked;

// eslint-disable-next-line
window.Clipboard = (function(window, document, navigator) {
  let textArea;
  function isOS() {
    return navigator.userAgent.match(/ipad|iphone/i);
  }
  function createTextArea(text) {
    textArea = document.createElement('textArea');
    textArea.style.visibility = 'none';
    textArea.value = text;
    document.body.appendChild(textArea);
  }
  function selectText() {
    let range;
    let selection;
    if (isOS()) {
      range = document.createRange();
      range.selectNodeContents(textArea);
      selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      textArea.setSelectionRange(0, 999999);
    } else {
      textArea.select();
    }
  }
  function copyToClipboard() {
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }

  // eslint-disable-next-line
  const copy = function (text) {
    createTextArea(text);
    selectText();
    copyToClipboard();
  };

  return {
    copy,
  };
})(window, document, navigator);

const globalComponents = [
  // Buttons
  { name: 'Button', path: 'Buttons/Button.vue' },
  { name: 'IconButton', path: 'Buttons/IconButton.vue' },
  { name: 'Toggle', path: 'Buttons/Toggle.vue' },
  // Containers
  { name: 'PageView', path: 'Containers/PageView.vue' },
  { name: 'PageTab', path: 'Containers/PageTab.vue' },
  { name: 'CardMinimal', path: 'Containers/CardMinimal.vue' },
  { name: 'CardMinimalTitle', path: 'Containers/CardMinimalTitle.vue' },
  { name: 'List', path: 'Containers/List.vue' },
  { name: 'Modal', path: 'Containers/Modal.vue' },
  { name: 'CardLink', path: 'Containers/CardLink.vue' },
  { name: 'InfoCard', path: 'Containers/InfoCard.vue' },
  // Form
  { name: 'FormCheckbox', path: 'Form/Checkbox.vue' },
  { name: 'FormColorInput', path: 'Form/ColorInput.vue' },
  { name: 'FormDropdown', path: 'Form/Dropdown.vue' },
  { name: 'FormInput', path: 'Form/Input.vue' },
  { name: 'FormInputText', path: 'Form/InputText.vue' },
  { name: 'FormRadio', path: 'Form/Radio.vue' },
  { name: 'FormTags', path: 'Form/Tags.vue' },
  { name: 'FormTextarea', path: 'Form/Textarea.vue' },
  { name: 'FormDate', path: 'Form/Date.vue' },
  // Other
  { name: 'Id', path: 'Id.vue' },
  { name: 'Graph', path: 'graph.vue' },
];

// eslint-disable-next-line
globalComponents.forEach(comp => Vue.component(`${comp.name}`, require(`./components/${comp.path}`).default));

const uiComponents = [
  'alert-error',
  'alert-info',
  'basic-panel',
  'dropdown',
  'dropdown-item',
  'page-header',
];

// eslint-disable-next-line
uiComponents.forEach(name => Vue.component(`${name}`, require(`./components/ui/${name}.vue`).default));

const i18n = new VueI18n({
  locale: 'en',
  messages: { en },
});

const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;

Vue.use(GoogleAnalytics, {
  id: process.env.VUE_APP_GOOGLE_ANALYTICS,
  disabled: window[`ga-disable-${process.env.VUE_APP_GOOGLE_ANALYTICS}`] || dnt === '1' || dnt === 'yes',
  router,
});
Vue.use(queryHelper, router);

// eslint-disable-next-line
new Vue({
  el: '.app',
  store,
  router,
  i18n,
  render: h => h(App),
  created() {
    // eslint-disable-next-line
    window.disableGa = function() { this.$googleanalytics.disable(); }.bind(this);
  },
});
